import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { DownloadIcon, ShieldCheckIcon } from 'lucide-react';
const sampleData = [{
  business: 'Smith & Associates CPA',
  owner: 'John Smith',
  title: 'Managing Partner',
  phone: '(212) 555-••••',
  email: 'john.smith@smithcpa.co',
  address: '123 Wall St, New York, NY 10005',
  website: 'smithcpa.co',
  employees: '15-25',
  revenue: '$2M-$5M',
  specialization: 'Tax Preparation'
}, {
  business: 'Global Accounting Solutions',
  owner: 'Sarah Chen',
  title: 'Founder & CEO',
  phone: '(310) 555-••••',
  email: 'sarah.chen@globalacc.net',
  address: '456 Sunset Blvd, Los Angeles, CA 90028',
  website: 'globalaccounting.net',
  employees: '50-100',
  revenue: '$10M+',
  specialization: 'Audit Services'
}, {
  business: 'Midwest Financial Group',
  owner: 'David Lee',
  title: 'Senior Partner',
  phone: '(312) 555-••••',
  email: 'david.lee@midwestfg.org',
  address: '789 Michigan Ave, Chicago, IL 60611',
  website: 'midwestfinancial.org',
  employees: '25-50',
  revenue: '$5M-$10M',
  specialization: 'Bookkeeping'
}, {
  business: 'Horizon Tax Services',
  owner: 'Emily White',
  title: 'Owner',
  phone: '(713) 555-••••',
  email: 'emily.white@horizontax.com',
  address: '321 Main St, Houston, TX 77002',
  website: 'horizontax.com',
  employees: '5-10',
  revenue: '$1M-$2M',
  specialization: 'Tax Consulting'
}, {
  business: 'Pacific Coast CPAs',
  owner: 'Michael Green',
  title: 'Principal CPA',
  phone: '(415) 555-••••',
  email: 'michael.green@pccoast.io',
  address: '654 Market St, San Francisco, CA 94102',
  website: 'pacificcoastcpa.io',
  employees: '10-15',
  revenue: '$2M-$5M',
  specialization: 'Forensic Accounting'
}];
export function AccountingDataPreview() {
  const [activeRow, setActiveRow] = useState<number | null>(null);
  return <section className="py-24 bg-white" data-id="element-303">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-304">
        <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-305">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 leading-tight" data-id="element-306">
            Preview our verified accounting owner database
          </h2>
          <p className="text-xl text-slate-600" data-id="element-307">
            See real accounting business data with verified owner contact
            information. Our accounting email list includes verified direct
            emails, verified phones, and 30+ enriched fields for every firm.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-xl overflow-hidden" data-id="element-308">
          <div className="flex justify-between items-center p-6 border-b border-slate-200 bg-slate-50" data-id="element-309">
            <h3 className="text-xl font-bold text-slate-900" data-id="element-310">
              Sample verified accounting database
            </h3>
            <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors" data-id="element-311">
              <DownloadIcon className="w-4 h-4" data-id="element-312" />
              Get full access
            </a>
          </div>

          <div className="overflow-x-auto" data-id="element-313">
            <table className="w-full text-sm text-left" data-id="element-314">
              <thead data-id="element-315">
                <tr className="text-slate-500 font-semibold border-b border-slate-200 bg-slate-50" data-id="element-316">
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-317">Business name</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-318">
                    Verified owner / contact
                  </th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-319">Title</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-320">
                    <div className="flex items-center gap-2" data-id="element-321">
                      Email
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" data-id="element-322">
                        Verified
                      </span>
                    </div>
                  </th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-323">
                    <div className="flex items-center gap-2" data-id="element-324">
                      Phone
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700" data-id="element-325">
                        Verified
                      </span>
                    </div>
                  </th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-326">Address</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-327">Website</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-328">Employees</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-329">Revenue</th>
                  <th className="py-4 px-4 whitespace-nowrap" data-id="element-330">
                    Specialization
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-331">
                {sampleData.map((row, idx) => <motion.tr key={idx} onHoverStart={() => setActiveRow(idx)} onHoverEnd={() => setActiveRow(null)} className={`transition-all duration-200 cursor-pointer ${activeRow === idx ? 'bg-blue-50' : 'hover:bg-slate-50'}`} data-id="element-332">
                    <td className="py-4 px-4 font-semibold text-slate-900 whitespace-nowrap" data-id="element-333">
                      {row.business}
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-334">
                      {row.owner}
                    </td>
                    <td className="py-4 px-4 text-slate-500 whitespace-nowrap" data-id="element-335">
                      {row.title}
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap" data-id="element-336">
                      <div className="flex items-center gap-1.5" data-id="element-337">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-green-600 flex-shrink-0" data-id="element-338" />
                        <span className="text-blue-600 font-medium" data-id="element-339">
                          {row.email}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-4 whitespace-nowrap" data-id="element-340">
                      <div className="flex items-center gap-1.5 text-slate-600" data-id="element-341">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-green-600 flex-shrink-0" data-id="element-342" />
                        <span data-id="element-343">{row.phone}</span>
                      </div>
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-344">
                      {row.address}
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-345">
                      {row.website}
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-346">
                      {row.employees}
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-347">
                      {row.revenue}
                    </td>
                    <td className="py-4 px-4 text-slate-600 whitespace-nowrap" data-id="element-348">
                      {row.specialization}
                    </td>
                  </motion.tr>)}
              </tbody>
            </table>
          </div>

          <div className="p-4 bg-slate-50 border-t border-slate-200 text-center" data-id="element-349">
            <p className="text-sm text-slate-500" data-id="element-350">
              Showing 5 of <strong className="text-slate-900" data-id="element-351">250,000+</strong>{' '}
              verified accounting firm records.{' '}
              <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="text-blue-600 font-semibold hover:underline" data-id="element-352">
                Sign up to unlock full database →
              </a>
            </p>
          </div>
        </div>
      </div>
    </section>;
}